import {
  isTrackReference,
  type TrackReferenceOrPlaceholder,
  useConnectionState,
  useLocalParticipant,
  useRoomContext,
  useTracks,
} from '@livekit/components-react'
import { ConnectionState, RoomEvent, Track } from 'livekit-client'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'

import { report } from './diagnostics'
import { t } from './i18n'
import { detectPipMode, type PipMode, pickPipFocus, shouldSuspectAutoBlocked } from './pip'

type Credential = { code?: string | null; reconnectToken?: string | null }

/**
 * Picture-in-picture lifecycle. Policy lives in pip.ts; this hook owns the
 * window, the portal target, the MediaSession wiring and every teardown path.
 *
 * Two entry roads, ONE enter():
 * - MANUAL — the control-bar button (a real user gesture).
 * - AUTO — Chrome's video-conferencing auto-PiP: registering the MediaSession
 *   'enterpictureinpicture' action makes Chrome call US when the user leaves
 *   the tab (Chromium 120+ desktop, page actively capturing cam/mic, site
 *   setting "Automatic picture-in-picture" allowing). The handler runs with
 *   enough activation to open the window — that is the entire point of the
 *   API. There is deliberately NO visibilitychange → requestWindow path:
 *   without a gesture it can only reject, and it would fight Chrome's own
 *   auto-close on refocus.
 *
 * Every attempt carries a GENERATION token (attempt ownership): teardown,
 * exit, disconnect and unmount all advance the generation, and an attempt
 * whose token went stale while awaiting the browser must bury its own result
 * (close the window it just opened) rather than resurrect a dead PiP — a
 * teacher moving the class to another calling app in the same second Chrome auto-enters must
 * not be left with an orphan floating window. (CODEX-PIP A1.)
 *
 * Teardown is a funnel: the window's own X, Chrome auto-closing on tab
 * refocus, "back to tab", our exit(), room disconnect, Classroom unmount —
 * all land in ONE idempotent teardown. Document mode hears it via the PiP
 * window's 'pagehide' (scoped to THAT window); video mode is event-driven off
 * 'leavepictureinpicture' — the browser's event is authoritative, because an
 * exitPictureInPicture() that rejects leaves the window OPEN and the UI must
 * keep saying so. (CODEX-PIP A3.)
 */

/**
 * The floating window's entire stylesheet, injected inline (CSP allows
 * 'unsafe-inline' styles). Deliberately NOT the app CSS: the PiP document
 * holds one bespoke tile, and copying 3.5k lines of theme for it buys nothing
 * but drift risk.
 */
const PIP_CSS = `
  :root { color-scheme: dark; }
  html, body { margin: 0; height: 100%; background: #0F1226; overflow: hidden; }
  body { font: 600 13px/1.4 system-ui, -apple-system, 'Segoe UI', sans-serif; color: #eef2ff; }
  .pip { position: relative; height: 100%; min-height: 260px; display: grid; place-items: center; }
  .pip__video { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain; }
  .pip__video--cover { object-fit: cover; }
  .pip__empty { text-align: center; padding: 12px; color: #9aa7c7; }
  .pip__empty strong { display: block; color: #eef2ff; margin-bottom: 4px; font-size: 14px; }
  .pip__label {
    position: absolute; inset-inline-start: 8px; bottom: 8px; max-width: calc(100% - 16px);
    padding: 3px 10px; border-radius: 999px; background: rgb(5 8 15 / 78%);
    font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    border: 1px solid rgb(148 163 210 / 28%);
  }
  .pip--speaking .pip__label { border-color: #4ade80; }
  .pip__banner {
    position: absolute; top: 8px; inset-inline-start: 50%; transform: translateX(-50%);
    padding: 3px 10px; border-radius: 999px; background: rgb(120 53 15 / 88%);
    font-size: 11.5px; white-space: nowrap;
  }
  [dir='rtl'] .pip__banner { transform: translateX(50%); }
  .pip__controls {
    position: absolute; inset-inline-start: 0; inset-inline-end: 0; bottom: 0;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    padding: 8px 10px; min-height: 60px; box-sizing: border-box;
    background: rgb(5 8 15 / 82%); border-block-start: 1px solid rgb(255 255 255 / 8%);
    -webkit-backdrop-filter: blur(10px); backdrop-filter: blur(10px);
  }
  .pip__controls button {
    flex: 0 0 44px; width: 44px; height: 44px; border-radius: 50%;
    border: 1px solid rgb(255 255 255 / 12%); background: rgb(255 255 255 / 10%);
    color: #eef2ff; display: grid; place-items: center; cursor: pointer;
    font-size: 18px; position: relative;
  }
  .pip__controls button:disabled { opacity: 0.45; cursor: default; }
  .pip__controls button:hover:not(:disabled) { background: rgb(255 255 255 / 18%); }
  .pip__controls button.danger { background: linear-gradient(180deg, #f04446, var(--eh-error, #de1f1f)); border-color: transparent; color: #fff; }
  .pip__controls button.accent { background: linear-gradient(180deg, #ffa04a, var(--eh-secondary, #ff7f1c)); border-color: transparent; color: #22160a; }
  .pip__controls .count {
    position: absolute; top: -2px; inset-inline-end: -2px; min-width: 18px; height: 18px;
    padding: 0 4px; border-radius: 999px; background: var(--eh-error, #de1f1f); color: #fff;
    font-size: 10px; font-weight: 700; display: grid; place-items: center;
  }
  .pip__status {
    position: absolute; top: 8px; inset-inline-start: 50%; transform: translateX(-50%);
    padding: 3px 10px; border-radius: 999px; background: rgb(120 53 15 / 88%);
    font-size: 11.5px; white-space: nowrap;
  }
  [dir='rtl'] .pip__status { transform: translateX(50%); }
  .pip__status--alert { background: rgb(222 31 31 / 70%); }
`

export interface PipApi {
  mode: PipMode
  /** A floating window (either flavour) is showing right now. */
  active: boolean
  /** enter() would actually do something — gates the button and nothing else. */
  canEnter: boolean
  toggle: () => void
  /** Document-mode portal target; null unless active. */
  portalTarget: HTMLElement | null
  /** What the window shows; recomputed live (share > teacher > sticky). */
  focus: TrackReferenceOrPlaceholder | undefined
  /**
   * video-mode only: the offscreen <video> PipStage keeps attached so a tap
   * can enter element-PiP without waiting on metadata (an await there would
   * spend the gesture's activation).
   */
  videoRef: React.RefObject<HTMLVideoElement>
  /** Chrome's auto-PiP looks blocked: the user hid the tab twice but it never fired. */
  autoSuspect: boolean
}

export function usePictureInPicture(viewerIsTeacher: boolean, credential: Credential): PipApi {
  const room = useRoomContext()
  const connectionState = useConnectionState()
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled } = useLocalParticipant()
  const mode = useMemo(detectPipMode, [])

  const [active, setActive] = useState(false)
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null)
  // Safari 14–17.1 passes the standard-API probe but refuses PiP for
  // MediaStream-backed video (fixed in 17.2, WebKit 252765). The refusal is
  // permanent for the session — one NotSupportedError retires the feature so
  // the button does not sit there pretending. (CODEX-PIP E2.)
  const [videoModeDead, setVideoModeDead] = useState(false)
  // Did the user hide the tab twice without seeing an auto-PiP? Surface help.
  const [autoSuspect, setAutoSuspect] = useState(false)

  const pipWindowRef = useRef<Window | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const activeRef = useRef(false)
  /** In-flight guard: auto + manual can race a tab-switch; one request only. */
  const enteringRef = useRef(false)
  /** Attempt ownership: advanced by every teardown; stale awaits self-bury. */
  const generationRef = useRef(0)
  const credentialRef = useRef(credential)
  credentialRef.current = credential

  // Auto-PiP counters: hidden = tab hidden while the handler is registered;
  // autoEnter = successful auto-opens. Reported once per session when suspect.
  const hiddenCountRef = useRef(0)
  const autoEnterCountRef = useRef(0)
  const reportedAutoSuspectRef = useRef(false)

  // Camera pubs INCLUDING muted/unsubscribed ones and participants who never
  // published a camera at all (placeholders): the window must be able to say
  // "Camera off" about the teacher rather than pretend the class is empty.
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  )
  // Stickiness remembers the last COMMITTED camera focus (ref writes happen
  // in an effect, not during render — a discarded concurrent render must not
  // move the window's attention). While a share owns the window `previous`
  // stays the camera, so when the share ends the teacher's watched student does
  // not hop to whoever sorts first.
  const lastCameraFocusRef = useRef<TrackReferenceOrPlaceholder | undefined>(undefined)
  const focus = pickPipFocus(tracks, viewerIsTeacher, lastCameraFocusRef.current)
  const focusRef = useRef(focus)
  useEffect(() => {
    focusRef.current = focus
    if (focus && focus.source === Track.Source.Camera) lastCameraFocusRef.current = focus
  })

  const teardown = useCallback(() => {
    generationRef.current += 1 // strand any attempt still awaiting the browser
    // Back to main window on a deliberate exit — best effort, and only after
    // teardown so focus cannot resurrect a portal into a closed document.
    if (activeRef.current) {
      try {
        window.focus()
      } catch {
        /* some browsers block cross-window focus; nothing to recover */
      }
    }
    pipWindowRef.current = null
    // SYNCHRONOUS portal unmount, while the PiP document is still alive:
    // 'pagehide' fires BEFORE Chromium destroys the document, but a queued
    // React commit lands AFTER — and removing portal nodes from a dead
    // document is how a PiP close white-screens the whole classroom. If React
    // is mid-render (unmount path), flushSync warns and defers — safe there,
    // because that path unmounts the portal itself while the window is open.
    try {
      flushSync(() => setPortalTarget(null))
    } catch {
      setPortalTarget(null)
    }
    if (activeRef.current) {
      activeRef.current = false
      setActive(false)
      report(credentialRef.current, {
        stage: 'permissions',
        outcome: 'ok',
        permission: 'pip=closed',
      })
    }
  }, [])

  const enter = useCallback(
    async (source: 'auto' | 'manual') => {
      if (activeRef.current || enteringRef.current) return
      // Never open an empty window: a floating black box reads as breakage.
      if (!focusRef.current) return
      enteringRef.current = true
      const generation = generationRef.current

      if (mode === 'document') {
        const dpp = window.documentPictureInPicture
        if (!dpp || dpp.window) {
          // One PiP window per browsing session; if something owns it, yield.
          enteringRef.current = false
          return
        }
        try {
          // FIRST await in this function, straight off the guard checks: the
          // MediaSession handler's activation must be spent HERE, not on
          // state updates or telemetry.
          const pipWindow = await dpp.requestWindow({ width: 420, height: 300 })
          if (generationRef.current !== generation) {
            // The class ended / we tore down while the browser was opening
            // this window. It belongs to a dead attempt: bury it.
            pipWindow.close()
            return
          }
          pipWindowRef.current = pipWindow
          const style = pipWindow.document.createElement('style')
          style.textContent = PIP_CSS
          pipWindow.document.head.appendChild(style)
          pipWindow.document.title = t('pip.title')
          // The window inherits nothing — Arabic sessions need their direction.
          pipWindow.document.documentElement.dir = document.documentElement.dir
          pipWindow.document.documentElement.lang = document.documentElement.lang
          // EVERY close road fires pagehide: the X, Chrome's auto-close on tab
          // refocus, "back to tab", and our own close(). Scoped to THIS
          // window: a stale event must never tear down a successor. (A2.)
          pipWindow.addEventListener(
            'pagehide',
            () => {
              if (pipWindowRef.current === pipWindow) teardown()
            },
            { once: true },
          )
          setPortalTarget(pipWindow.document.body)
          activeRef.current = true
          setActive(true)
          report(credentialRef.current, {
            stage: 'permissions',
            outcome: 'ok',
            permission: `pip=enter-${source}`,
          })
          if (source === 'auto') autoEnterCountRef.current += 1
          else autoEnterCountRef.current = 0
          hiddenCountRef.current = 0
          setAutoSuspect(false)
        } catch {
          // Denied (setting off, another window raced us) — PiP is an extra,
          // never an error the user must deal with.
          report(credentialRef.current, {
            stage: 'permissions',
            outcome: 'error',
            permission: `pip=open-failed-${source}`,
          })
        } finally {
          enteringRef.current = false
        }
        return
      }

      if (mode === 'video') {
        const el = videoRef.current
        // Metadata must already be there: PipStage keeps this element attached
        // precisely so no await sits between the tap and the request.
        if (!el || el.readyState < 1) {
          // The refusal is invisible to the user (button did nothing) — it
          // must not also be invisible to us.
          report(credentialRef.current, {
            stage: 'permissions',
            outcome: 'error',
            permission: `pip=open-failed-not-ready-${source}`,
          })
          enteringRef.current = false
          return
        }
        try {
          // The browser's event is the single source of truth for element-PiP
          // ending — whatever the road (our exit(), the window chrome, the
          // element leaving the DOM).
          el.addEventListener(
            'leavepictureinpicture',
            () => {
              teardown()
            },
            { once: true },
          )
          await el.requestPictureInPicture()
          if (generationRef.current !== generation) {
            // Torn down while entering: leave no orphan floating frame.
            void document.exitPictureInPicture().catch(() => undefined)
            return
          }
          activeRef.current = true
          setActive(true)
          report(credentialRef.current, {
            stage: 'permissions',
            outcome: 'ok',
            permission: `pip=enter-${source}`,
          })
          if (source === 'auto') autoEnterCountRef.current += 1
          else autoEnterCountRef.current = 0
          hiddenCountRef.current = 0
          setAutoSuspect(false)
        } catch (error) {
          if ((error as { name?: string })?.name === 'NotSupportedError') {
            // Safari < 17.2 with a MediaStream source: permanent this session.
            setVideoModeDead(true)
          }
          report(credentialRef.current, {
            stage: 'permissions',
            outcome: 'error',
            permission: `pip=open-failed-${source}`,
          })
        } finally {
          enteringRef.current = false
        }
        return
      }

      enteringRef.current = false
    },
    [mode, teardown],
  )

  const exit = useCallback(() => {
    if (mode === 'document') {
      // Teardown FIRST (synchronously unmounts the portal from the still-open
      // window), THEN close. The reverse order removes React nodes from a
      // document that close() already destroyed.
      const pipWindow = pipWindowRef.current
      teardown()
      pipWindow?.close()
      return
    }
    // Video mode is EVENT-DRIVEN: request the exit and let
    // 'leavepictureinpicture' run the teardown. If the request rejects the
    // window is still open, and `active` keeps truthfully saying so.
    if (document.pictureInPictureElement) {
      void document.exitPictureInPicture().catch(() => undefined)
      return
    }
    // Nothing is actually in PiP (browser already closed it, event lost):
    // reconcile the books.
    teardown()
  }, [mode, teardown])

  const toggle = useCallback(() => {
    if (activeRef.current) exit()
    else void enter('manual')
  }, [enter, exit])

  // Element-PiP can outlive its content: if the focus track vanishes while
  // the floating frame is up (camera muted, student left), Chromium exits on
  // element removal but old Safari can keep a frozen frame forever — so ask
  // for the exit explicitly. PipStage keeps the donor mounted while active
  // precisely so this runs against a live element. (CODEX-PIP G1.)
  const focusHasLiveVideo = Boolean(
    focus && isTrackReference(focus) && !focus.publication.isMuted && focus.publication.track,
  )
  useEffect(() => {
    if (mode !== 'video' || !active) return
    if (focusHasLiveVideo) return
    exit()
  }, [mode, active, focusHasLiveVideo, exit])

  // ---- Chrome's auto-enter: register while (and only while) we are a live,
  // connected conference. Registered even without a current focus — the focus
  // usually appears moments later and enter() refuses empty windows anyway.
  const connected = connectionState === ConnectionState.Connected
  useEffect(() => {
    if (mode === 'none' || !connected) return
    if (!('mediaSession' in navigator)) return
    const session = navigator.mediaSession
    try {
      // Throws TypeError on Chromium < 120 where the action name is unknown —
      // that IS the feature detection.
      session.setActionHandler('enterpictureinpicture', () => void enter('auto'))
    } catch {
      return
    }
    return () => {
      try {
        session.setActionHandler('enterpictureinpicture', null)
      } catch {
        /* symmetric with registration */
      }
    }
  }, [mode, connected, enter])

  // Mic + camera controls on the PiP/media-hub chrome. Same command path as
  // the control-bar buttons — reads live state, never a closure.
  useEffect(() => {
    if (!connected || !localParticipant) return
    if (!('mediaSession' in navigator)) return
    const session = navigator.mediaSession
    try {
      session.setActionHandler('togglemicrophone', () => {
        localParticipant
          .setMicrophoneEnabled(!localParticipant.isMicrophoneEnabled)
          .catch(() => undefined /* same silent policy as the bar's toggle */)
      })
    } catch {
      return
    }
    try {
      session.setActionHandler('togglecamera', () => {
        localParticipant.setCameraEnabled(!localParticipant.isCameraEnabled).catch(() => undefined)
      })
    } catch {
      /* some engines do not support togglecamera */
    }
    return () => {
      try {
        session.setActionHandler('togglemicrophone', null)
      } catch {
        /* symmetric with registration */
      }
      try {
        session.setActionHandler('togglecamera', null)
      } catch {
        /* symmetric */
      }
    }
  }, [connected, localParticipant])

  // Keep the browser's media-hub chips truthful.
  const micEnabled = isMicrophoneEnabled
  const cameraEnabled = isCameraEnabled
  useEffect(() => {
    if (!('mediaSession' in navigator)) return
    try {
      navigator.mediaSession.setMicrophoneActive?.(micEnabled)
    } catch {
      /* advisory chrome, nothing to recover */
    }
    try {
      navigator.mediaSession.setCameraActive?.(cameraEnabled)
    } catch {
      /* advisory chrome */
    }
  }, [micEnabled, cameraEnabled])

  // The class ending — disconnect, leave, moved to another calling app (Classroom unmounts,
  // room disconnects) — takes the floating window with it.
  useEffect(() => {
    if (!room) return
    const onDisconnected = () => exit()
    room.on(RoomEvent.Disconnected, onDisconnected)
    return () => {
      room.off(RoomEvent.Disconnected, onDisconnected)
    }
  }, [room, exit])
  // Unmount only — via a ref with EMPTY deps, so no future dependency churn
  // can ever run this cleanup mid-lesson and close a live window.
  const exitRef = useRef(exit)
  exitRef.current = exit
  useEffect(() => () => exitRef.current(), [])

  // What the button may promise. Document mode: any focus (the window can
  // honestly say "camera off"). Video mode: only a live, unmuted track on a
  // platform that has not permanently refused — element PiP has no custom
  // chrome to explain anything else with. (CODEX-PIP F1.)
  const canEnter =
    mode === 'document'
      ? Boolean(focus)
      : mode === 'video'
        ? focusHasLiveVideo && !videoModeDead
        : false

  // ---- Auto-PiP suspicion. Count tab hides while the MediaSession handler is
  // registered and a successful auto-enter has not fired. The handler is only
  // registered when connected, so the count is only meaningful then.
  useEffect(() => {
    if (mode !== 'document' || !connected) return
    const onVisibility = () => {
      if (!document.hidden || activeRef.current) return
      hiddenCountRef.current += 1
      const suspect = shouldSuspectAutoBlocked({
        mode,
        hiddenCount: hiddenCountRef.current,
        autoEnterCount: autoEnterCountRef.current,
      })
      if (suspect) {
        setAutoSuspect(true)
        if (!reportedAutoSuspectRef.current) {
          reportedAutoSuspectRef.current = true
          report(credentialRef.current, {
            stage: 'permissions',
            outcome: 'error',
            permission: 'pip=auto-never-fired',
          })
        }
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [mode, connected])

  return {
    mode: videoModeDead && mode === 'video' ? 'none' : mode,
    active,
    canEnter,
    toggle,
    portalTarget,
    focus,
    videoRef,
    autoSuspect,
  }
}
